/**
 * Data utilities combining Supabase and Yahoo Finance
 */

import { supabase } from './supabase'
import type { Portfolio, Holding, Transaction, InsertPortfolio } from './database.types'

// Re-export types for convenience
export type { Portfolio, Holding, Transaction }
import { fetchPriceInfo, fetchPriceHistory, normalizeToPence, fetchFxRateUsdGbp } from './yahooFinance'

/**
 * Fetch latest price from database for a ticker
 */
async function fetchPriceFromDb(ticker: string): Promise<{ pricePence: number; date: string } | null> {
  const { data, error } = await supabase
    .from('prices')
    .select('close_pence, date')
    .eq('ticker', ticker)
    .order('date', { ascending: false })
    .limit(1)
    .single()

  if (error || !data) return null
  const priceData = data as { close_pence: number; date: string }
  return { pricePence: priceData.close_pence, date: priceData.date }
}

/**
 * Fetch price history from database for a ticker
 */
async function fetchPriceHistoryFromDb(ticker: string): Promise<{ date: string; close: number }[]> {
  const { data, error } = await supabase
    .from('prices')
    .select('date, close_pence')
    .eq('ticker', ticker)
    .order('date', { ascending: true })

  if (error || !data) return []
  return (data as { date: string; close_pence: number }[]).map(p => ({ date: p.date, close: p.close_pence }))
}

// Portfolio with calculated values
export interface PortfolioWithValue extends Portfolio {
  totalValue: number
  costBasis: number
  gain: number
  gainPct: number
  dayChange: number
  dayChangePct: number
  mtdChange: number
  mtdChangePct: number
  ytdChange: number
  ytdChangePct: number
  holdingsValue: number
  holdings: HoldingWithValue[]
}

// Holding with calculated values
export interface HoldingWithValue extends Holding {
  currentPrice: number | null
  currentValue: number
  gain: number
  gainPct: number
  dayChange: number
  dayChangePct: number
  mtdChange: number
  mtdChangePct: number
  ytdChange: number
  ytdChangePct: number
  priceInPence: number | null
}

// Holding with transactions and price history
export interface HoldingDetail extends HoldingWithValue {
  transactions: Transaction[]
  priceHistory: { date: string; close: number }[]
  valueHistory: { date: string; value: number }[]
}

/**
 * Fetch all portfolios for the current user
 */
export async function fetchPortfolios(): Promise<Portfolio[]> {
  const { data, error } = await supabase
    .from('portfolios')
    .select('*')
    .order('name')

  if (error) throw error
  return data || []
}

/**
 * Create a new portfolio for the current user
 * Note: user_id is automatically set by RLS/trigger, but we include it for type safety
 */
export async function createPortfolio(
  name: string,
  options: {
    accountType?: string
    currency?: string
    cashBalance?: number
  } = {}
): Promise<Portfolio> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('User must be authenticated to create a portfolio')
  }

  const portfolio: InsertPortfolio = {
    user_id: user.id,
    name,
    account_type: options.accountType || null,
    currency: options.currency || 'GBP',
    cash_balance: options.cashBalance || 0,
  }

  const { data, error } = await supabase
    .from('portfolios')
    .insert(portfolio as never)
    .select()
    .single()

  if (error) throw error
  if (!data) throw new Error('Failed to create portfolio')

  return data
}

/**
 * Fetch portfolios with current values (requires fetching prices)
 */
export async function fetchPortfoliosWithValues(): Promise<PortfolioWithValue[]> {
  // For now, return portfolios without prices (will be enhanced later)
  const portfolios = await fetchPortfolios()
  
  const portfoliosWithValues: PortfolioWithValue[] = []
  
  for (const portfolio of portfolios) {
    const holdings = await fetchHoldingsWithValues(portfolio.id)
    
    const holdingsValue = holdings.reduce((sum, h) => sum + h.currentValue, 0)
    const costBasis = holdings.reduce((sum, h) => sum + h.cost_basis, 0)
    const totalValue = holdingsValue + portfolio.cash_balance
    const gain = holdingsValue - costBasis
    const gainPct = costBasis > 0 ? (gain / costBasis) * 100 : 0
    
    // Calculate day change
    const dayChange = holdings.reduce((sum, h) => sum + h.dayChange, 0)
    const previousDayValue = totalValue - dayChange
    const dayChangePct = previousDayValue > 0 ? (dayChange / previousDayValue) * 100 : 0
    
    // Calculate MTD change
    const mtdChange = holdings.reduce((sum, h) => sum + h.mtdChange, 0)
    const mtdBaseValue = totalValue - mtdChange
    const mtdChangePct = mtdBaseValue > 0 ? (mtdChange / mtdBaseValue) * 100 : 0
    
    // Calculate YTD change
    const ytdChange = holdings.reduce((sum, h) => sum + h.ytdChange, 0)
    const ytdBaseValue = totalValue - ytdChange
    const ytdChangePct = ytdBaseValue > 0 ? (ytdChange / ytdBaseValue) * 100 : 0
    
    portfoliosWithValues.push({
      ...portfolio,
      totalValue,
      costBasis,
      gain,
      gainPct,
      dayChange,
      dayChangePct,
      mtdChange,
      mtdChangePct,
      ytdChange,
      ytdChangePct,
      holdingsValue,
      holdings,
    })
  }
  
  return portfoliosWithValues
}

/**
 * Fetch holdings for a portfolio
 */
export async function fetchHoldings(portfolioId: string): Promise<Holding[]> {
  const { data, error } = await supabase
    .from('holdings')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .order('cost_basis', { ascending: false })
  
  if (error) throw error
  return data || []
}

/**
 * Calculate MTD and YTD changes from price history (Yahoo Finance)
 */
async function calculatePeriodChanges(
  ticker: string,
  currentValue: number,
  quantity: number
): Promise<{ mtdChange: number; mtdChangePct: number; ytdChange: number; ytdChangePct: number }> {
  try {
    // Get 1 year of price history
    const priceHistory = await fetchPriceHistory(ticker, '1y')
    if (priceHistory.length === 0) {
      return { mtdChange: 0, mtdChangePct: 0, ytdChange: 0, ytdChangePct: 0 }
    }
    
    const now = new Date()
    const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const ytdStart = new Date(now.getFullYear(), 0, 1)
    
    // Find closest prices to period starts
    const mtdPrice = findClosestPrice(priceHistory, mtdStart)
    const ytdPrice = findClosestPrice(priceHistory, ytdStart)
    
    // Calculate changes (prices are in native currency, convert to GBP pence equivalent)
    void priceHistory[priceHistory.length - 1].close // current price (reserved)

    let mtdChange = 0
    let mtdChangePct = 0
    if (mtdPrice) {
      const mtdValue = (mtdPrice * quantity) / 100
      mtdChange = currentValue - mtdValue
      mtdChangePct = mtdValue > 0 ? (mtdChange / mtdValue) * 100 : 0
    }
    
    let ytdChange = 0
    let ytdChangePct = 0
    if (ytdPrice) {
      const ytdValue = (ytdPrice * quantity) / 100
      ytdChange = currentValue - ytdValue
      ytdChangePct = ytdValue > 0 ? (ytdChange / ytdValue) * 100 : 0
    }
    
    return { mtdChange, mtdChangePct, ytdChange, ytdChangePct }
  } catch (error) {
    console.warn(`Failed to calculate period changes for ${ticker}:`, error)
    return { mtdChange: 0, mtdChangePct: 0, ytdChange: 0, ytdChangePct: 0 }
  }
}

/**
 * Calculate MTD and YTD changes from database price history
 */
async function calculatePeriodChangesFromDb(
  ticker: string,
  currentValue: number,
  quantity: number
): Promise<{ mtdChange: number; mtdChangePct: number; ytdChange: number; ytdChangePct: number }> {
  try {
    // Get price history from database
    const priceHistory = await fetchPriceHistoryFromDb(ticker)
    if (priceHistory.length === 0) {
      // Fall back to Yahoo Finance
      return calculatePeriodChanges(ticker, currentValue, quantity)
    }
    
    const now = new Date()
    const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const ytdStart = new Date(now.getFullYear(), 0, 1)
    
    // Find closest prices to period starts
    const mtdPrice = findClosestPrice(priceHistory, mtdStart)
    const ytdPrice = findClosestPrice(priceHistory, ytdStart)

    let mtdChange = 0
    let mtdChangePct = 0
    if (mtdPrice) {
      const mtdValue = (mtdPrice * quantity) / 100
      mtdChange = currentValue - mtdValue
      mtdChangePct = mtdValue > 0 ? (mtdChange / mtdValue) * 100 : 0
    }
    
    let ytdChange = 0
    let ytdChangePct = 0
    if (ytdPrice) {
      const ytdValue = (ytdPrice * quantity) / 100
      ytdChange = currentValue - ytdValue
      ytdChangePct = ytdValue > 0 ? (ytdChange / ytdValue) * 100 : 0
    }
    
    return { mtdChange, mtdChangePct, ytdChange, ytdChangePct }
  } catch (error) {
    console.warn(`Failed to calculate period changes from DB for ${ticker}:`, error)
    return { mtdChange: 0, mtdChangePct: 0, ytdChange: 0, ytdChangePct: 0 }
  }
}

/**
 * Find the closest price to a given date
 */
function findClosestPrice(priceHistory: { date: string; close: number }[], targetDate: Date): number | null {
  let closestPrice: number | null = null
  let closestDiff = Infinity
  
  for (const ph of priceHistory) {
    const phDate = new Date(ph.date)
    const diff = Math.abs(phDate.getTime() - targetDate.getTime())
    
    // Only use prices before or on the target date
    if (phDate <= targetDate && diff < closestDiff) {
      closestDiff = diff
      closestPrice = ph.close
    }
  }
  
  return closestPrice
}

/**
 * Fetch holdings with current prices
 * Tries database first (prices table), falls back to Yahoo Finance
 */
export async function fetchHoldingsWithValues(portfolioId: string): Promise<HoldingWithValue[]> {
  const holdings = await fetchHoldings(portfolioId)
  const fxRate = await fetchFxRateUsdGbp()
  
  const holdingsWithValues: HoldingWithValue[] = []
  
  for (const holding of holdings) {
    try {
      // Try database first (for UK funds that we fetch via HL/Morningstar)
      const dbPrice = await fetchPriceFromDb(holding.ticker)
      
      let priceInPence: number | null = null
      let dayChangePct = 0
      
      if (dbPrice) {
        priceInPence = dbPrice.pricePence
      } else {
        // Fall back to Yahoo Finance for stocks
        const priceInfo = await fetchPriceInfo(holding.ticker)
        priceInPence = priceInfo.currentPrice 
          ? normalizeToPence(priceInfo.currentPrice, holding.ticker, priceInfo.currency, fxRate)
          : null
        dayChangePct = priceInfo.dayChangePct || 0
      }
      
      const currentValue = priceInPence 
        ? (priceInPence * holding.quantity) / 100  // Convert pence to pounds
        : holding.cost_basis  // Fallback to cost basis
      
      const gain = currentValue - holding.cost_basis
      const gainPct = holding.cost_basis > 0 ? (gain / holding.cost_basis) * 100 : 0
      
      // Calculate day change based on percentage
      const previousValue = dayChangePct !== 0 
        ? currentValue / (1 + dayChangePct / 100)
        : currentValue
      const dayChange = currentValue - previousValue
      
      // Calculate MTD and YTD changes from database history
      const { mtdChange, mtdChangePct, ytdChange, ytdChangePct } = await calculatePeriodChangesFromDb(
        holding.ticker,
        currentValue,
        holding.quantity
      )
      
      holdingsWithValues.push({
        ...holding,
        currentPrice: priceInPence ? priceInPence / 100 : null,  // Convert to pounds for display
        priceInPence,
        currentValue,
        gain,
        gainPct,
        dayChange,
        dayChangePct,
        mtdChange,
        mtdChangePct,
        ytdChange,
        ytdChangePct,
      })
    } catch (error) {
      console.warn(`Failed to fetch price for ${holding.ticker}:`, error)
      
      // Fallback: show holding without current price
      holdingsWithValues.push({
        ...holding,
        currentPrice: null,
        priceInPence: null,
        currentValue: holding.cost_basis,
        gain: 0,
        gainPct: 0,
        dayChange: 0,
        dayChangePct: 0,
        mtdChange: 0,
        mtdChangePct: 0,
        ytdChange: 0,
        ytdChangePct: 0,
      })
    }
  }
  
  // Sort by value descending
  return holdingsWithValues.sort((a, b) => b.currentValue - a.currentValue)
}

/**
 * Build a timeline of cumulative quantity from transactions
 */
function buildQuantityTimeline(transactions: Transaction[]): Map<string, number> {
  const timeline = new Map<string, number>()
  let cumulative = 0

  // Sort by trade_date ascending
  const sorted = [...transactions].sort((a, b) =>
    a.trade_date.localeCompare(b.trade_date)
  )

  for (const tx of sorted) {
    if (tx.type === 'BUY' || tx.type === 'DRIP' || tx.type === 'NEW_ISSUE' || tx.type === 'CARD_WEB') {
      cumulative += tx.quantity || 0
    } else if (tx.type === 'SELL') {
      cumulative -= Math.abs(tx.quantity || 0)
    } else if (tx.type === 'TRANSFER') {
      // Transfer direction inferred from quantity sign
      cumulative += tx.quantity || 0
    } else if (tx.type === 'REFUND') {
      // Refund doesn't change quantity
    }
    timeline.set(tx.trade_date, cumulative)
  }

  return timeline
}

/**
 * Get quantity held on a specific date
 */
function getQuantityOnDate(timeline: Map<string, number>, date: string): number {
  let lastQty = 0
  for (const [txDate, qty] of timeline) {
    if (txDate > date) break
    lastQty = qty
  }
  return lastQty
}

/**
 * Get the most recent price on or before a target date
 */
function getMostRecentPrice(prices: { date: string; close_pence: number }[], targetDate: string): number | null {
  let lastPrice: number | null = null
  for (const p of prices) {
    if (p.date > targetDate) break
    lastPrice = p.close_pence
  }
  return lastPrice
}

/**
 * Fetch portfolio value history aggregated across all holdings
 */
export async function fetchPortfolioValueHistory(portfolioId: string): Promise<{ date: string; value: number }[]> {
  // Get all holdings
  const { data: holdings } = await supabase
    .from('holdings')
    .select('ticker')
    .eq('portfolio_id', portfolioId)
    .gt('quantity', 0)

  if (!holdings?.length) return []

  // Collect all price data and timelines for each holding
  const holdingData: {
    ticker: string
    prices: { date: string; close_pence: number }[]
    timeline: Map<string, number>
  }[] = []

  const allDates = new Set<string>()

  for (const h of holdings as { ticker: string }[]) {
    // Get transactions for this holding
    const { data: transactions } = await supabase
      .from('transactions')
      .select('*')
      .eq('portfolio_id', portfolioId)
      .eq('ticker', h.ticker)
      .order('trade_date')

    const timeline = buildQuantityTimeline(transactions as Transaction[] || [])

    // Get prices
    const { data: prices } = await supabase
      .from('prices')
      .select('date, close_pence')
      .eq('ticker', h.ticker)
      .order('date')

    if (!prices || prices.length === 0) continue

    const priceArray = prices as { date: string; close_pence: number }[]

    // Collect all dates
    for (const p of priceArray) {
      allDates.add(p.date)
    }

    holdingData.push({
      ticker: h.ticker,
      prices: priceArray,
      timeline,
    })
  }

  if (holdingData.length === 0) return []

  // Sort all unique dates
  const sortedDates = Array.from(allDates).sort()

  // Calculate total value for each date using carried-forward prices
  const allValues = new Map<string, number>()

  for (const date of sortedDates) {
    let totalValue = 0

    for (const holding of holdingData) {
      const qty = getQuantityOnDate(holding.timeline, date)
      if (qty <= 0) continue

      // Get most recent price on or before this date
      const price = getMostRecentPrice(holding.prices, date)
      if (price) {
        totalValue += (price * qty) / 100 // Convert to pounds
      }
    }

    if (totalValue > 0) {
      allValues.set(date, totalValue)
    }
  }

  // Convert to sorted array
  return Array.from(allValues.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, value]) => ({ date, value }))
}

/**
 * Fetch portfolio value history from the cached portfolio_value_history table
 */
export async function fetchPortfolioValueHistoryFromDb(portfolioId: string): Promise<{ date: string; value: number }[]> {
  const { data, error } = await supabase
    .from('portfolio_value_history')
    .select('date, value_gbp')
    .eq('portfolio_id', portfolioId)
    .order('date', { ascending: true })

  if (error || !data || data.length === 0) {
    // Fallback to computed history
    return fetchPortfolioValueHistory(portfolioId)
  }

  return (data as { date: string; value_gbp: number }[]).map(row => ({
    date: row.date,
    value: row.value_gbp,
  }))
}

/**
 * Fetch sector allocation for a portfolio
 * Uses the sector column on holdings, falling back to ticker-based inference
 */
export async function fetchSectorAllocation(portfolioId: string): Promise<{ sector: string; value: number; pct: number }[]> {
  const { data: holdingsData, error } = await supabase
    .from('holdings')
    .select('ticker, name, quantity, cost_basis, sector')
    .eq('portfolio_id', portfolioId)
    .gt('quantity', 0)

  if (error || !holdingsData || holdingsData.length === 0) return []

  // Get current prices for each holding
  const holdingsWithPrices = await Promise.all(
    (holdingsData as { ticker: string; name: string; quantity: number; cost_basis: number; sector: string | null }[]).map(async (h) => {
      const price = await fetchPriceFromDb(h.ticker)
      const currentValue = price
        ? (price.pricePence * h.quantity) / 100
        : h.cost_basis
      const sector = h.sector || inferSector(h.ticker, h.name)
      return { ticker: h.ticker, sector, value: currentValue }
    })
  )

  const totalValue = holdingsWithPrices.reduce((sum, h) => sum + h.value, 0)

  // Group by sector
  const sectorMap = new Map<string, number>()
  for (const h of holdingsWithPrices) {
    sectorMap.set(h.sector, (sectorMap.get(h.sector) || 0) + h.value)
  }

  return Array.from(sectorMap.entries())
    .map(([sector, value]) => ({
      sector,
      value,
      pct: totalValue > 0 ? (value / totalValue) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value)
}

/**
 * Infer sector from ticker/name
 */
function inferSector(ticker: string, name: string): string {
  const n = name.toLowerCase()
  const t = ticker.toLowerCase()
  if (t.includes('gold') || t.includes('silver') || t.includes('tin') || n.includes('gold') || n.includes('silver') || n.includes('commodity')) return 'Commodities'
  if (t.includes('oil') || n.includes('oil') || n.includes('gas') || n.includes('energy')) return 'Energy'
  if (n.includes('bank') || n.includes('insur') || n.includes('financial') || n.includes('paypoint')) return 'Financials'
  if (n.includes('tech') || n.includes('software') || n.includes('semiconductor') || n.includes('nvidia') || n.includes('amd') || n.includes('apple') || n.includes('alphabet') || n.includes('amazon') || n.includes('netflix') || n.includes('tesla') || n.includes('zoom') || n.includes('mobileye') || n.includes('artificial intelligence') || n.includes('cyber')) return 'Technology'
  if (n.includes('pharma') || n.includes('health') || n.includes('bio') || n.includes('gsk') || n.includes('haleon') || n.includes('agios')) return 'Healthcare'
  if (n.includes('retail') || n.includes('shop') || n.includes('restaurant') || n.includes('food') || n.includes('greggs') || n.includes('whitbread') || n.includes('marks & spencer')) return 'Consumer'
  if (n.includes('aero') || n.includes('airline') || n.includes('rolls royce') || n.includes('bae')) return 'Aerospace & Defence'
  if (n.includes('mining') || n.includes('mineral') || n.includes('bhp') || n.includes('andrada') || n.includes('yellow cake')) return 'Mining'
  if (n.includes('auto') || n.includes('car') || n.includes('vehicle') || n.includes('aston martin') || n.includes('rivian')) return 'Automotive'
  if (n.includes('tobacco') || n.includes('cigarette')) return 'Consumer Staples'
  if (t.includes('bond') || n.includes('bond') || n.includes('fixed income') || n.includes('corporate bond')) return 'Fixed Income'
  if (t.startsWith('0p') || t.includes('fund') || n.includes('fund') || n.includes('trust') || n.includes('etf') || n.includes('accumulation')) return 'Funds & ETFs'
  if (n.includes('infrastructure') || n.includes('network') || n.includes('utilities')) return 'Infrastructure'
  return 'Other'
}

/**
 * Fetch single holding with detail
 */
export async function fetchHoldingDetail(
  portfolioId: string,
  ticker: string
): Promise<HoldingDetail | null> {
  // Get holding
  const { data: holding, error: holdingError } = await supabase
    .from('holdings')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .eq('ticker', ticker)
    .single()
  
  if (holdingError || !holding) return null
  const h = holding as Holding

  // Get transactions
  const { data: transactions, error: txError } = await supabase
    .from('transactions')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .eq('ticker', ticker)
    .order('trade_date', { ascending: false })

  if (txError) throw txError

  // Get price info
  const fxRate = await fetchFxRateUsdGbp()
  const priceInfo = await fetchPriceInfo(ticker).catch(() => null)
  const priceInPence = priceInfo?.currentPrice
    ? normalizeToPence(priceInfo.currentPrice, ticker, priceInfo.currency || 'USD', fxRate)
    : null

  const currentValue = priceInPence
    ? (priceInPence * h.quantity) / 100
    : h.cost_basis

  const gain = currentValue - h.cost_basis
  const gainPct = h.cost_basis > 0 ? (gain / h.cost_basis) * 100 : 0

  const dayChangePct = priceInfo?.dayChangePct || 0
  const previousValue = dayChangePct !== 0
    ? currentValue / (1 + dayChangePct / 100)
    : currentValue
  const dayChange = currentValue - previousValue

  // Get price history from database first, fall back to Yahoo
  let priceHistory = await fetchPriceHistoryFromDb(ticker)
  if (priceHistory.length === 0) {
    priceHistory = await fetchPriceHistory(ticker, 'max').catch(() => [])
  }

  // Build quantity timeline from transactions
  const qtyTimeline = buildQuantityTimeline(transactions as Transaction[] || [])

  const valueHistory = priceHistory.map(ph => {
    const qty = getQuantityOnDate(qtyTimeline, ph.date)
    return {
      date: ph.date,
      value: (ph.close * qty) / 100,
    }
  })

  // Calculate period changes
  const { mtdChange, mtdChangePct, ytdChange, ytdChangePct } = await calculatePeriodChanges(
    ticker,
    currentValue,
    h.quantity
  )

  return {
    ...h,
    currentPrice: priceInfo?.currentPrice || null,
    priceInPence,
    currentValue,
    gain,
    gainPct,
    dayChange,
    dayChangePct,
    mtdChange,
    mtdChangePct,
    ytdChange,
    ytdChangePct,
    transactions: transactions as Transaction[] || [],
    priceHistory,
    valueHistory,
  }
}

/**
 * Fetch transactions for a portfolio
 */
export async function fetchTransactions(portfolioId: string): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .order('trade_date', { ascending: false })
  
  if (error) throw error
  return data || []
}

/**
 * Import transactions from parsed CSV
 */
export async function importTransactions(
  portfolioId: string,
  transactions: {
    ticker: string
    type: string
    tradeDate: string
    settleDate: string | null
    quantity: number | null
    pricePence: number | null
    valueGbp: number
    reference: string
    description: string
  }[]
): Promise<{ success: number; errors: string[] }> {
  const errors: string[] = []
  let success = 0

  for (const tx of transactions) {
    const { error } = await supabase
      .from('transactions')
      .upsert({
        portfolio_id: portfolioId,
        ticker: tx.ticker,
        type: tx.type,
        trade_date: tx.tradeDate,
        settle_date: tx.settleDate,
        quantity: tx.quantity,
        price_pence: tx.pricePence,
        value_gbp: tx.valueGbp,
        reference: tx.reference,
        description: tx.description,
      } as never, {
        onConflict: 'portfolio_id,reference'
      })

    if (error) {
      errors.push(`${tx.reference}: ${error.message}`)
    } else {
      success++
    }
  }

  return { success, errors }
}

/**
 * Update or create holdings based on transactions
 * This recalculates holdings from all transactions for a ticker
 */
export async function recalculateHoldings(portfolioId: string, ticker: string): Promise<void> {
  const { data: transactions, error } = await supabase
    .from('transactions')
    .select('*')
    .eq('portfolio_id', portfolioId)
    .eq('ticker', ticker)
    .order('trade_date', { ascending: true })

  if (error) throw error
  if (!transactions || transactions.length === 0) return

  // Calculate position
  let quantity = 0
  let costBasis = 0

  for (const tx of transactions as Transaction[]) {
    const txQty = tx.quantity || 0
    const txValue = tx.value_gbp

    switch (tx.type) {
      case 'BUY':
      case 'DRIP':
      case 'REBATE':
      case 'CARD_WEB':
        quantity += txQty
        costBasis += Math.abs(txValue)
        break
      case 'SELL':
        // Calculate avg cost per unit for the shares being sold
        const avgCostPerUnit = quantity > 0 ? costBasis / quantity : 0
        quantity -= txQty
        costBasis -= txQty * avgCostPerUnit
        break
      case 'DIVIDEND':
        // Dividends don't change quantity but add to cost basis (return of capital)
        costBasis -= txValue  // txValue is positive for dividends
        break
      case 'TRANSFER':
        // Transfers: positive quantity = in, negative = out
        quantity += txQty
        if (txValue !== 0) {
          costBasis += Math.abs(txValue)
        }
        break
      case 'REFUND':
        // Refund: adds cash back, no quantity change
        costBasis -= Math.abs(txValue)
        break
    }
  }

  // Ensure non-negative
  quantity = Math.max(0, quantity)
  costBasis = Math.max(0, costBasis)

  // Get stock name from first transaction
  const stockName = (transactions[0] as Transaction)?.description?.split('@')[0].trim() || ticker

  // Upsert holding
  const { error: upsertError } = await supabase
    .from('holdings')
    .upsert({
      portfolio_id: portfolioId,
      ticker,
      name: stockName,
      quantity,
      cost_basis: costBasis,
    } as never, {
      onConflict: 'portfolio_id,ticker'
    })

  if (upsertError) throw upsertError
}

/**
 * Add a ticker mapping
 */
export async function addTickerMapping(
  hlName: string,
  ticker: string,
  exchange?: string,
  currency: string = 'GBP',
  assetType?: string
): Promise<void> {
  const { error } = await supabase
    .from('ticker_mappings')
    .upsert({
      hl_name: hlName,
      ticker,
      exchange: exchange || null,
      currency,
      asset_type: assetType || null,
    } as never, {
      onConflict: 'hl_name'
    })

  if (error) throw error
}

/**
 * Get unmapped stock names (HL names not in ticker_mappings)
 */
export async function getUnmappedStocks(hlNames: string[]): Promise<string[]> {
  const { data: mappings, error } = await supabase
    .from('ticker_mappings')
    .select('hl_name')
    .in('hl_name', hlNames)

  if (error) throw error

  const mappedNames = new Set((mappings as { hl_name: string }[] || []).map(m => m.hl_name))
  return hlNames.filter(name => !mappedNames.has(name))
}

/**
 * Fetch all ticker mappings from the database
 */
export async function fetchTickerMappings(): Promise<Record<string, string>> {
  const { data: mappings, error } = await supabase
    .from('ticker_mappings')
    .select('hl_name, ticker')

  if (error) throw error

  const result: Record<string, string> = {}
  for (const mapping of (mappings as { hl_name: string; ticker: string }[] || [])) {
    result[mapping.hl_name] = mapping.ticker
  }

  return result
}
